import { useRef, useCallback } from 'react'

const ATTACHMENT_SELECTORS = [
  '.str-chat__attachment-preview-item',
  '[class*="attachment-preview-item"]',
  '[class*="attachment-preview-image"]',
  '[class*="attachment-preview-file"]',
] as const

const RELOAD_CLICK_DISTANCE = 30

type AttachmentInfo = {
  id: string
  asset_url?: string
  image_url?: string
  thumb_url?: string
  file_url?: string
}

const hasAttachmentUrl = (attachment: AttachmentInfo): boolean => {
  return !!(attachment.asset_url || attachment.image_url || attachment.thumb_url || (attachment as any).file_url)
}

const findAttachmentElement = (attachmentList: Element, index: number): HTMLElement | null => {
  for (const selector of ATTACHMENT_SELECTORS) {
    const items = attachmentList.querySelectorAll(selector)
    if (items[index]) {
      return items[index] as HTMLElement
    }
  }
  return null
}

const removeLoadingClasses = (item: HTMLElement) => {
  item.classList.remove('str-chat__attachment-preview-image-loading')
  const loadingElements = item.querySelectorAll('[class*="loading"]')
  loadingElements.forEach((el) => {
    el.classList.remove('str-chat__attachment-preview-image-loading')
  })
}

const createReloadClickHandler = (
  item: HTMLElement,
  attachmentId: string,
  onReload: (id: string) => void,
) => {
  return (e: MouseEvent) => {
    const rect = item.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    const clickX = e.clientX
    const clickY = e.clientY
    const distance = Math.sqrt(Math.pow(clickX - centerX, 2) + Math.pow(clickY - centerY, 2))

    if (distance < RELOAD_CLICK_DISTANCE) {
      e.stopPropagation()
      onReload(attachmentId)
    }
  }
}

export const useAttachmentOfflineStyle = (onReload: (id: string) => void) => {
  const handlersRef = useRef<Map<string, (e: MouseEvent) => void>>(new Map())

  const applyOfflineStyle = useCallback(
    (attachment: AttachmentInfo, index: number, shouldBeOffline: boolean) => {
      const attachmentList = document.querySelector('.str-chat__attachment-preview-list')
      if (!attachmentList) return

      const item = findAttachmentElement(attachmentList, index)
      if (!item) return

      const isCurrentlyOffline = item.classList.contains('str-chat__attachment-preview-item--offline')
      const attachmentId = attachment.id || `attachment-${index}`

      if (shouldBeOffline && !isCurrentlyOffline) {
        item.classList.add('str-chat__attachment-preview-item--offline')
        removeLoadingClasses(item)

        if (!item.dataset.reloadHandler || item.dataset.reloadHandler !== attachmentId) {
          item.dataset.reloadHandler = attachmentId

          const handler = createReloadClickHandler(item, attachmentId, onReload)
          handlersRef.current.set(attachmentId, handler)
          item.addEventListener('click', handler, { once: false })
        }
      } else if (!shouldBeOffline && isCurrentlyOffline) {
        item.classList.remove('str-chat__attachment-preview-item--offline')

        const handler = handlersRef.current.get(attachmentId)
        if (handler) {
          item.removeEventListener('click', handler)
          handlersRef.current.delete(attachmentId)
        }
      }
    },
    [onReload],
  )

  const removeOfflineStyle = useCallback((attachmentId: string, attachmentIndex: number) => {
    const attachmentList = document.querySelector('.str-chat__attachment-preview-list')
    if (!attachmentList) return

    for (const selector of ATTACHMENT_SELECTORS) {
      const items = attachmentList.querySelectorAll(selector)
      const item = items[attachmentIndex] as HTMLElement
      if (item?.classList.contains('str-chat__attachment-preview-item--offline')) {
        item.classList.remove('str-chat__attachment-preview-item--offline')

        const handler = handlersRef.current.get(attachmentId)
        if (handler) {
          item.removeEventListener('click', handler)
          handlersRef.current.delete(attachmentId)
        }
        break
      }
    }
  }, [])

  const cleanup = useCallback(() => {
    handlersRef.current.forEach((handler, attachmentId) => {
      const attachmentList = document.querySelector('.str-chat__attachment-preview-list')
      if (!attachmentList) return

      for (const selector of ATTACHMENT_SELECTORS) {
        const items = attachmentList.querySelectorAll(selector)
        items.forEach((item) => {
          if (item.dataset.reloadHandler === attachmentId) {
            item.removeEventListener('click', handler)
          }
        })
      }
    })
    handlersRef.current.clear()
  }, [])

  return {
    applyOfflineStyle,
    removeOfflineStyle,
    cleanup,
  }
}

